import {
  extractTechSettings,
  initialFormData,
  initialUIState,
} from './internals';
import type { FormStateSlice, RenderStateCreator } from './types';

export const createFormStateSlice: RenderStateCreator<FormStateSlice> = (
  set,
  get
) => ({
  // Initial state
  ...initialUIState,
  formData: { ...initialFormData },
  defaultValues: null,
  techBaseline: null,
  parentRenderMode: 'explore',
  selectedReferences: [],
  currentContext: null,
  lastInitializedKey: null,

  updateFormData: (partial) => {
    set((state) => ({
      formData: { ...state.formData, ...partial },
    }));
    // Synchronous persist. localStorage writes are cheap and writing on every
    // keystroke means closing the sheet / navigating back within milliseconds
    // of the last edit can't drop the draft. Previously this was a 600 ms
    // debounce that lost data on quick exit and on continuous typing.
    get().persistDraft();
  },

  setDefaultValues: (values, parentMode) =>
    set((state) => {
      // Refine intent is per-render — never persisted into defaults so the
      // next render doesn't inherit it. Strip mode/parent_render_id before
      // they can re-enter formData via initial load or resetChangesToDefaults.
      const sanitized = { ...values };
      delete sanitized.mode;
      delete sanitized.parent_render_id;

      const fullDefaultValues = { ...initialFormData, ...sanitized };
      const isInitialLoad = !state.defaultValues;

      return {
        defaultValues: fullDefaultValues,
        // Re-snapshot the tech baseline to the new defaults' tech group: this
        // is the submitted form's render style (== the current slot), so the
        // Output dot reads clean right after submit. Without this the baseline
        // would keep the stale load-time slot and light the dot post-submit.
        techBaseline: extractTechSettings(fullDefaultValues),
        // Update only when caller knows the source render's mode (post-submit
        // path); otherwise keep the previous value so the legacy
        // `setDefaultValues(submitted)` calls don't accidentally clobber it.
        parentRenderMode: parentMode ?? state.parentRenderMode,
        formData: isInitialLoad
          ? { ...initialFormData, ...sanitized }
          : state.formData,
      };
    }),

  resetFormData: () =>
    set((state) => ({
      formData: state.defaultValues
        ? {
            ...initialFormData,
            ...state.defaultValues,
            // Refine intent is ephemeral — defaults never carry it.
            mode: 'explore',
            parent_render_id: null,
          }
        : { ...initialFormData },
    })),

  // Selected references are UI-only state. The server has no inline per-render
  // `references` attribute (it rejects it as an extra param) — project-level
  // refs travel via `*_project_reference_ids`, so this never feeds the payload.
  setSelectedReferences: (refs) =>
    set({
      selectedReferences: refs,
    }),

  setIsDerivative: (isDerivative) =>
    set((state) => ({
      formData: { ...state.formData, is_derivative: isDerivative },
    })),

  // UI actions
  setSelectedRender: (renderId) =>
    set({
      selectedRenderId: renderId,
    }),

  openRenderDetails: (renderId) =>
    set({
      showRenderDetails: true,
      selectedRenderId: renderId,
    }),

  closeRenderDetails: () =>
    set({
      showRenderDetails: false,
      selectedRenderId: null,
    }),

  reset: () =>
    set({
      ...initialUIState,
      formData: { ...initialFormData },
      defaultValues: null,
      techBaseline: null,
      selectedReferences: [],
      currentContext: null,
      lastInitializedKey: null,
    }),
});
